import {
  Component,
  OnInit,
  HostListener,
  ElementRef,
  NgZone,
  inject,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
  AyudaService,
  BundleAyuda,
  CategoriaAyuda,
  GrupoAyuda,
  SubcategoriaAyuda,
  ArticuloRef,
  ResultadoBusqueda,
} from '../service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { API } from '../../../../../environments/environment';

// Mapeo de categoría de ayuda → módulo + tipos de cliente permitidos
const PERMISOS_CATEGORIA: Record<
  string,
  { modulo?: string; tiposCliente?: string[] }
> = {
  trackers: { modulo: 'Vehículos', tiposCliente: ['Mayorista', 'Minorista'] },
  'configurar-alarma': {
    modulo: 'Alarmas',
    tiposCliente: ['Mayorista', 'Minorista'],
  },
  'comunicadores-alarma': {
    modulo: 'Alarmas',
    tiposCliente: ['Mayorista', 'Minorista'],
  },
  'paneles-alarma': {
    modulo: 'Alarmas',
    tiposCliente: ['Mayorista', 'Minorista'],
  },
  // Operación de Luminarias: visible solo para clientes con el módulo Luminarias
  // (sin restricción de tipo de cliente — aplica a municipios/intendencias).
  'operacion-luminarias': { modulo: 'Luminarias' },
  // Operación de la plataforma (repo gestion-doc-operacion): alta de clientes,
  // alarmas y vehículos. Es trabajo de quien administra su propia cartera, así
  // que no se muestra a clientes Final.
  'primeros-pasos': { tiposCliente: ['Mayorista', 'Minorista'] },
  'operacion-alarmas': {
    modulo: 'Alarmas',
    tiposCliente: ['Mayorista', 'Minorista'],
  },
  'operacion-vehiculos': {
    modulo: 'Vehículos',
    tiposCliente: ['Mayorista', 'Minorista'],
  },
};

interface TrackerCard {
  ref: ArticuloRef;
  categoria: CategoriaAyuda;
}

@Component({
  selector: 'app-ayuda',
  templateUrl: './ayuda.component.html',
  styleUrls: ['./ayuda.component.scss'],
  standalone: false,
})
export class AyudaComponent implements OnInit {
  private ayudaService = inject(AyudaService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  helper = inject(HelperService);
  private el = inject(ElementRef);
  private zone = inject(NgZone);

  public bundle?: BundleAyuda;
  public categoriasVisibles: CategoriaAyuda[] = [];
  public sinContenido = false;

  // Grupos (pestañas): "Operar el sistema" / "Configurar equipos". Sólo los que
  // tienen alguna categoría visible para este cliente.
  public grupos: GrupoAyuda[] = [];
  public grupoActivo?: string;
  public categoriasDelGrupo: CategoriaAyuda[] = [];

  // Vista actual: indice → subcategoria → articulo. Se deriva de la URL.
  public vista: 'indice' | 'subcategoria' | 'articulo' = 'indice';
  public contenidoActual?: string;
  public tituloActual?: string;
  public resumenActual?: string;
  private slugActual?: string;

  // Navegación drill-down
  public categoriaActual?: CategoriaAyuda;
  public subcategoriaActual?: SubcategoriaAyuda;

  // Búsqueda
  public queryBusqueda = '';
  public resultadosBusqueda: ResultadoBusqueda[] = [];
  public buscando = false;

  private mermaidLoaded = false;

  // Interceptar clicks en links internos dentro del markdown renderizado:
  // links relativos a otro artículo (.md) y links a una categoría entera
  // ("#categoria/<id>").
  @HostListener('click', ['$event'])
  onLinkClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const anchor = target.closest('a');
    if (!anchor || this.vista !== 'articulo') return;

    const href = anchor.getAttribute('href');
    if (!href) return;

    // Link a una categoría entera. El markdown lo escribe como
    // "#categoria/configurar-alarma"; el navegador puede prefijarlo con la ruta
    // actual, así que se busca el marcador en cualquier posición.
    const marcador = '#categoria/';
    const posMarcador = href.indexOf(marcador);
    if (posMarcador !== -1) {
      event.preventDefault();
      event.stopPropagation();
      this.abrirCategoria(href.slice(posMarcador + marcador.length));
      return;
    }

    if (!href.endsWith('.md')) return;

    event.preventDefault();
    event.stopPropagation();

    const slug = this.resolverSlugDesdeLink(href);
    if (slug && this.bundle?.articulos[slug]) {
      // Un link cruzado puede saltar de categoría y de grupo.
      const ubicacion = this.ubicarSlug(slug);
      this.irA({ g: ubicacion?.categoria.grupo, art: slug });
    }
  }

  async ngOnInit() {
    try {
      this.bundle = await this.ayudaService.getBundle();
      this.filtrarCategorias();
      // La URL es la fuente de verdad de la vista: así el botón atrás del
      // navegador sube un nivel en lugar de salir de la ayuda, y un artículo se
      // puede compartir por link.
      this.route.queryParamMap.subscribe((params) =>
        this.aplicarEstadoDesdeUrl(params),
      );
    } catch (error) {
      this.helper.notifError(error);
    }
  }

  private filtrarCategorias() {
    if (!this.bundle) return;
    const tipoCliente = HelperService.getTipoCliente();

    // El bundle puede traer `tiposCliente` por categoría o subcategoría (lo
    // declara el índice y la API ya lo aplicó). Se vuelve a chequear acá para
    // que un bundle cacheado no muestre algo que no corresponde.
    const permitidoPorTipo = (tipos?: string[]) =>
      !tipos?.length || tipos.includes(tipoCliente);

    this.categoriasVisibles = this.bundle.categorias
      .filter((cat) => {
        if (!permitidoPorTipo(cat.tiposCliente)) return false;

        const permisos = PERMISOS_CATEGORIA[cat.id];
        if (!permisos) return true; // Sin restricción

        if (
          permisos.modulo &&
          !this.helper.tieneModulo(permisos.modulo as any)
        ) {
          return false;
        }
        if (
          permisos.tiposCliente &&
          !permisos.tiposCliente.includes(tipoCliente)
        ) {
          return false;
        }
        return true;
      })
      .map((cat) => ({
        ...cat,
        subcategorias: cat.subcategorias.filter((sub) =>
          permitidoPorTipo(sub.tiposCliente),
        ),
      }))
      .filter((cat) => cat.subcategorias.length > 0);

    // Sólo se ofrecen los grupos que quedaron con contenido.
    this.grupos = (this.bundle.grupos ?? []).filter((g) =>
      this.categoriasVisibles.some((c) => c.grupo === g.id),
    );

    this.sinContenido = this.categoriasVisibles.length === 0;
  }

  // ── Estado desde la URL ────────────────────────────────────────────────────

  private aplicarEstadoDesdeUrl(params: ParamMap) {
    this.setGrupo(params.get('g'));

    const art = params.get('art');
    const sub = params.get('sub');

    this.resultadosBusqueda = [];
    this.queryBusqueda = '';

    if (art && this.mostrarArticulo(art)) return;
    if (sub && this.mostrarSubcategoria(sub)) return;
    this.mostrarIndice();
  }

  private setGrupo(id: string | null) {
    const valido =
      id && this.grupos.some((g) => g.id === id) ? id : this.grupos[0]?.id;
    this.grupoActivo = valido;
    // Sin grupos (bundle viejo) se muestran todas las categorías juntas.
    this.categoriasDelGrupo = this.grupoActivo
      ? this.categoriasVisibles.filter((c) => c.grupo === this.grupoActivo)
      : this.categoriasVisibles;
  }

  private mostrarArticulo(slug: string): boolean {
    const contenido = this.bundle?.articulos[slug];
    if (!contenido) return false;

    const ubicacion = this.ubicarSlug(slug);
    if (!ubicacion) return false; // no visible para este cliente

    this.categoriaActual = ubicacion.categoria;
    this.subcategoriaActual =
      ubicacion.subcategoria.articulos.length > 1
        ? ubicacion.subcategoria
        : undefined;
    this.resumenActual = ubicacion.articulo.resumen;
    this.contenidoActual = this.conImagenesAbsolutas(contenido.contenido);
    this.tituloActual = contenido.titulo;
    this.slugActual = slug;
    this.vista = 'articulo';
    window.scrollTo(0, 0);
    this.renderizarMermaid();
    return true;
  }

  private mostrarSubcategoria(ref: string): boolean {
    const [idCategoria, idSubcategoria] = ref.split('/');
    const categoria = this.categoriasVisibles.find((c) => c.id === idCategoria);
    const subcategoria = categoria?.subcategorias.find(
      (s) => s.id === idSubcategoria,
    );
    if (!categoria || !subcategoria) return false;

    this.categoriaActual = categoria;
    this.subcategoriaActual = subcategoria;
    this.contenidoActual = undefined;
    this.tituloActual = undefined;
    this.slugActual = undefined;
    this.vista = 'subcategoria';
    window.scrollTo(0, 0);
    return true;
  }

  private mostrarIndice() {
    this.vista = 'indice';
    this.contenidoActual = undefined;
    this.tituloActual = undefined;
    this.slugActual = undefined;
    this.categoriaActual = undefined;
    this.subcategoriaActual = undefined;
  }

  // Deja la vista reflejada en la URL. Los params que no se pasan se limpian.
  private irA(destino: {
    g?: string | null;
    sub?: string | null;
    art?: string | null;
  }) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        g: destino.g ?? this.grupoActivo ?? null,
        sub: destino.sub ?? null,
        art: destino.art ?? null,
      },
    });
  }

  // Ubica un slug (categoria/subcategoria/articulo) en el árbol visible.
  private ubicarSlug(slug: string):
    | {
        categoria: CategoriaAyuda;
        subcategoria: SubcategoriaAyuda;
        articulo: ArticuloRef;
      }
    | undefined {
    for (const categoria of this.categoriasVisibles) {
      for (const subcategoria of categoria.subcategorias) {
        const articulo = subcategoria.articulos.find((a) => a.slug === slug);
        if (articulo) return { categoria, subcategoria, articulo };
      }
    }
    return undefined;
  }

  private refDeSubcategoria(
    cat?: CategoriaAyuda,
    sub?: SubcategoriaAyuda,
  ): string | null {
    return cat && sub ? `${cat.id}/${sub.id}` : null;
  }

  // ── Navegación (la dispara el template) ───────────────────────────────────

  // Título del grupo activo, para el breadcrumb.
  public get tituloGrupoActivo(): string | undefined {
    return this.grupos.find((g) => g.id === this.grupoActivo)?.titulo;
  }

  public seleccionarGrupo(id: string) {
    if (id === this.grupoActivo) return;
    this.irA({ g: id });
  }

  // Abrir artículo directamente desde el índice (subcategoría con 1 artículo)
  abrirArticulo(tracker: TrackerCard) {
    this.irA({ g: tracker.categoria.grupo, art: tracker.ref.slug });
  }

  // Abrir subcategoría (drill-down a lista de artículos)
  abrirSubcategoria(cat: CategoriaAyuda, sub: SubcategoriaAyuda) {
    this.irA({ g: cat.grupo, sub: this.refDeSubcategoria(cat, sub) });
  }

  // Abrir artículo desde la vista de subcategoría
  abrirArticuloDesdeSubcategoria(art: ArticuloRef) {
    this.irA({
      sub: this.refDeSubcategoria(
        this.categoriaActual,
        this.subcategoriaActual,
      ),
      art: art.slug,
    });
  }

  volverAlIndice() {
    this.irA({});
  }

  volverASubcategoria() {
    if (this.subcategoriaActual) {
      this.irA({
        sub: this.refDeSubcategoria(
          this.categoriaActual,
          this.subcategoriaActual,
        ),
      });
    } else {
      this.volverAlIndice();
    }
  }

  volverAtras() {
    if (this.vista === 'articulo' && this.subcategoriaActual) {
      this.volverASubcategoria();
    } else {
      this.volverAlIndice();
    }
  }

  // Abre una categoría del índice, desde un link "#categoria/<id>" o desde el
  // breadcrumb.
  public abrirCategoria(idCategoria: string) {
    const cat = this.categoriasVisibles.find((c) => c.id === idCategoria);
    if (!cat) {
      this.helper.notifError(
        'Esa sección de la ayuda no está disponible para tu cliente',
      );
      return;
    }

    // Con una sola subcategoría no tiene sentido volver al índice: se entra directo.
    if (cat.subcategorias.length === 1) {
      const sub = cat.subcategorias[0];
      if (sub.articulos.length === 1) {
        this.abrirArticulo({ ref: sub.articulos[0], categoria: cat });
      } else {
        this.abrirSubcategoria(cat, sub);
      }
      return;
    }

    this.irA({ g: cat.grupo });
    // Esperar el render del índice para poder scrollear a la categoría.
    setTimeout(() => {
      const destino = this.el.nativeElement.querySelector(
        `#cat-${idCategoria}`,
      ) as HTMLElement | null;
      if (destino)
        destino.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else window.scrollTo(0, 0);
    });
  }

  async buscar() {
    const q = this.queryBusqueda?.trim();
    if (!q || q.length < 2) {
      this.resultadosBusqueda = [];
      return;
    }

    this.buscando = true;
    try {
      const res = await this.ayudaService.buscar(q);
      // La API ya recorta por tenant, pero el filtro por módulos contratados y
      // tipo de cliente sigue siendo del front: sin esto aparecen resultados
      // que `mostrarArticulo()` después se niega a abrir.
      this.resultadosBusqueda = res.resultados.filter((r) =>
        this.ubicarSlug(r.slug),
      );
    } catch (error) {
      this.helper.notifError(error);
    } finally {
      this.buscando = false;
    }
  }

  abrirDesdeResultado(resultado: ResultadoBusqueda) {
    const ubicacion = this.ubicarSlug(resultado.slug);
    this.irA({ g: ubicacion?.categoria.grupo, art: resultado.slug });
  }

  /**
   * Resuelve un href relativo de un link .md a un slug del bundle.
   */
  private resolverSlugDesdeLink(href: string): string | null {
    if (!this.slugActual) return null;

    const MAPEO_CARPETAS: Record<string, string> = {
      comunicadores: 'comunicadores-alarma',
      paneles: 'paneles-alarma',
      'alarmas-integradas': 'configurar-alarma',
    };

    const slugParts = this.slugActual.split('/');
    slugParts.pop();
    const hrefParts = href.replace(/\.md$/, '').split('/');

    const resolved: string[] = [...slugParts];
    for (const part of hrefParts) {
      if (part === '..') {
        resolved.pop();
      } else if (part !== '.') {
        resolved.push(part);
      }
    }

    const lastPart = resolved[resolved.length - 1];
    if (lastPart === '' || !lastPart) resolved.pop();

    let candidato = resolved.join('/');

    for (const [carpetaFuente, categoriaBundle] of Object.entries(
      MAPEO_CARPETAS,
    )) {
      if (candidato.startsWith(carpetaFuente + '/')) {
        candidato = candidato.replace(
          carpetaFuente + '/',
          categoriaBundle + '/',
        );
        break;
      }
    }

    if (this.bundle?.articulos[candidato]) return candidato;
    return null;
  }

  // Absolutiza las rutas de imagen del markdown ANTES de renderizarlo. Si se deja
  // el src relativo, ngx-markdown lo escribe tal cual y el navegador lo pide
  // contra el origen de la app (404) antes de que reescribirImagenes() lo corrija.
  // Idempotente: no toca lo que ya es absoluto.
  private conImagenesAbsolutas(md: string): string {
    if (!md) return md;
    return md.replace(
      /\]\(\.?\/?(imagenes\/[^)\s]+)/g,
      `](${API}/ayuda-static/$1`,
    );
  }

  // Red de seguridad para el HTML ya renderizado: apunta cualquier imagen
  // relativa que haya quedado a los estáticos servidos por la API. Idempotente.
  private reescribirImagenes(container: HTMLElement) {
    const imgs = container.querySelectorAll('img');
    imgs.forEach((img) => {
      const raw = img.getAttribute('src');
      if (!raw) return;
      if (/^(https?:)?\/\//.test(raw) || raw.startsWith('data:')) return;
      if (!raw.includes('imagenes/')) return;
      const limpio = raw.replace(/^\.?\//, '');
      img.setAttribute('src', `${API}/ayuda-static/${limpio}`);
    });
  }

  private async renderizarMermaid() {
    // Esperar a que ngx-markdown renderice el HTML
    await new Promise((r) => setTimeout(r, 100));

    const container = this.el.nativeElement as HTMLElement;

    // Reescribir imágenes relativas del manual a la URL estática de la API.
    // El markdown referencia "imagenes/..." de forma relativa; la app corre en
    // otro origen que la API, por eso hay que apuntarlas a <API>/ayuda-static/.
    this.reescribirImagenes(container);

    const bloques = container.querySelectorAll('code.language-mermaid');
    if (bloques.length === 0) return;

    // Cargar mermaid dinámicamente solo la primera vez
    if (!this.mermaidLoaded) {
      const mermaidModule = await import('mermaid');
      mermaidModule.default.initialize({
        startOnLoad: false,
        theme: 'default',
        securityLevel: 'loose',
      });
      this.mermaidLoaded = true;
    }

    const mermaid = (await import('mermaid')).default;

    this.zone.runOutsideAngular(async () => {
      for (let i = 0; i < bloques.length; i++) {
        const code = bloques[i] as HTMLElement;
        const pre = code.parentElement;
        if (!pre || pre.tagName !== 'PRE') continue;

        const definition = code.textContent || '';
        try {
          const id = `mermaid-${Date.now()}-${i}`;
          const { svg } = await mermaid.render(id, definition);
          const div = document.createElement('div');
          div.className = 'mermaid-diagram';
          div.innerHTML = svg;
          pre.replaceWith(div);
        } catch {
          // Si falla el rendering, dejar el bloque de código tal cual
        }
      }
    });
  }

  copiarTexto(texto: string) {
    navigator.clipboard.writeText(texto).then(() => {
      this.helper.notifSuccess('Copiado al portapapeles');
    });
  }
}
